import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SignalsService } from '@frontendlib/common/services/signals.service';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { SignalType } from '@sharedlib/common/types/signal-type';
import type { SignalBillingCollectionRefused } from '@sharedlib/dtos/dto/events/SignalBillingCollectionRefused';
import { RETRY_REFUSAL_TOAST } from './payment-retry-refusal';
import { PendingChargeService } from './pending-charge.service';

/** Ends a pending recovery charge the card-update collection refused, so Billing's status banners return. */
@Injectable({ providedIn: 'root' })
export class CollectionRefusalService {
  private readonly signalsService = inject(SignalsService);
  private readonly pendingCharge = inject(PendingChargeService);
  private readonly toaster = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.signalsService
      .getSignal(SignalType.BILLING_COLLECTION_REFUSED)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((signal) => this.handle(signal));
  }

  private handle({ accountId, refusal }: SignalBillingCollectionRefused): void {
    const charge = this.pendingCharge.pending();
    // An upgrade's charge is a plan change, which this collection never runs for.
    if (!charge || charge.reason === 'trial_upgrade' || this.pendingCharge.capture()?.accountId !== accountId) {
      return;
    }
    this.pendingCharge.clear();
    this.toaster.show({ text: RETRY_REFUSAL_TOAST[refusal] });
  }
}
